import React from "react";
import { stylex } from "./stylex";
import { cssVariables } from "./css_variables";
import { Text } from "./text";
import { Pressable } from "./pressable";

interface Option<TValue = any> {
  label: string;
  value: TValue;
}

interface SegmentedControlProps<TValue = any> {
  testID?: string;
  value?: TValue;
  onChange?: (value: TValue) => void;
  options?: Option<TValue>[];
}

export function SegmentedControl(props: SegmentedControlProps) {
  const { value, onChange = () => {}, options = [], testID } = props;

  return (
    <div style={styles("root")} data-testid={testID}>
      {options.map((option, index) => {
        const first = index === 0;
        const last = index === options.length - 1;
        const selected = value === option.value;

        return (
          <Pressable
            key={option.value}
            aria-pressed={selected}
            style={styles(
              "control",
              selected && "selected",
              first && "first",
              last && "last"
            )}
            onPress={() => onChange(option.value)}
          >
            <Text color="primary">{option.label}</Text>
          </Pressable>
        );
      })}
    </div>
  );
}

const styles = stylex.create({
  // Equal segments as wide as the widest label. A flex row with `flex: 1`
  // segments splits the *summed* label widths evenly when the control is
  // content-sized, so a long label beside a short one wraps onto two lines;
  // fr columns size every segment to the widest label instead.
  root: {
    display: "grid",
    gridAutoFlow: "column",
    gridAutoColumns: "1fr",
  },
  control: {
    borderRight: `1px solid ${cssVariables.borderColorPrimary}`,
    borderTop: `1px solid ${cssVariables.borderColorPrimary}`,
    borderBottom: `1px solid ${cssVariables.borderColorPrimary}`,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "white",
    height: 40,
    paddingLeft: 24,
    paddingRight: 24,
    whiteSpace: "nowrap",
  },
  first: {
    borderLeft: `1px solid ${cssVariables.borderColorPrimary}`,
    borderTopLeftRadius: cssVariables.radius,
    borderBottomLeftRadius: cssVariables.radius,
  },
  last: {
    borderTopRightRadius: cssVariables.radius,
    borderBottomRightRadius: cssVariables.radius,
  },
  selected: {
    backgroundColor: cssVariables.backgroundLightPrimary,
  },
});
